import { useContext, useMemo } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';

import DASHBOARD_ICON from '../assets/icons/ComniqaiIcon-V3.0/analytics-icon.svg';
import CHATBOT_ICON from '../assets/icons/ComniqaiIcon-V3.0/trainbot-icon.svg';
import LEAD_ICON from '../assets/icons/ComniqaiIcon-V3.0/lead-icon.svg';
import INTEGRATION_ICON from '../assets/icons/ComniqaiIcon-V3.0/integration-icon.svg';
import PRICING_ICON from '../assets/icons/ComniqaiIcon-V3.0/pricing-icon.svg';
import '../assets/styles/Sidebar.css';
import COMNIQAI_LOGO from '../assets/images/comniqAI/comniq-logo.svg';
import CHAT_ICON from '../assets/icons/ComniqaiIcon-V2.0/conversations-icon.svg';
import DOCS_ICON from '../assets/icons/ComniqaiIcon-V2.0/wordcount-icon.svg';
import PLAN_ICON from '../assets/icons/ComniqaiIcon-V3.0/plan-icon.svg';
import LEAP_ICON from '../assets/icons/ComniqaiIcon-V3.0/leap-icon.svg';
import SubContext from '../contexts/SubContext';
import { PATHS } from '../routes/paths';
import { getPlanLevel, PLAN_LEVELS } from '../API/Constant';
import useProgress from '../hooks/useProgress';
import ProgressBar from './ProgressBar';

// ---------------- Hover Handlers ----------------
const handleMouseOver = (e) => {
  const h2 = e.currentTarget.querySelector('h2');
  const img = e.currentTarget.querySelector('img');
  h2.style.color = '#ffffff';
  h2.style.opacity = 1;
  img.style.opacity = 1;
};

const handleMouseOut = (e) => {
  const h2 = e.currentTarget.querySelector('h2');
  const img = e.currentTarget.querySelector('img');
  h2.style.color = '';
  h2.style.opacity = 0.8;
  img.style.opacity = 0.8;
};

// ---------------- Sidebar Component ----------------
const Sidebar = () => {
  const location = useLocation();
  const Navigate = useNavigate();
  const subsFlag = useContext(SubContext);
  const { progress, urlStats, isProgressActive, showPopup } = useProgress();

  const subscriptionInfo = subsFlag?.subscriptionFlag?.user?.subscription ?? null;
  const subscriptionName = subscriptionInfo?.name ?? 'Free';
  const planLevel = useMemo(() => getPlanLevel(subscriptionName), [subscriptionName]);

  const isActive = (path) => (location.pathname === path ? 'active-border-left' : '');

  return (
    <div className='sidenav'>
      <div className='sidebar__container h-100'>
        {/* Logo */}
        <div className='logo-icon'>
          <NavLink to={planLevel >= PLAN_LEVELS.Starter ? PATHS.DASHBOARD : PATHS.CHATBOT}>
            <img src={COMNIQAI_LOGO} alt='logo' className='full-logo' />
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className='sidebar-nav-content'>
          <ul className='d-flex flex-column m-0'>
            {/* Analytics → Starter and above */}
            {planLevel >= PLAN_LEVELS.Scale && (
              <li className={`sidebar-icon ${isActive(PATHS.DASHBOARD)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                <NavLink to={PATHS.DASHBOARD}>
                  <img src={DASHBOARD_ICON} alt='dashboard-icon' />
                  <h2 className={`hover-effect outfit-medium ${isActive(PATHS.DASHBOARD) && 'hoverActive'}`}>Analytics</h2>
                </NavLink>
              </li>
            )}

            {/* L.E.A.P → Pro and above */}
            {planLevel >= PLAN_LEVELS.Growth && (
              <li className={`sidebar-icon ${isActive(PATHS.LEAP)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                <NavLink to={PATHS.LEAP}>
                  <img src={LEAP_ICON} alt='leap-icon' />
                  <h2 className={`hover-effect outfit-medium d-flex align-items-center justify-content-between w-100 ${isActive(PATHS.LEAP) && 'hoverActive'}`}>
                    L.E.A.P
                    <span className='badge rounded-pill outfit-regular sidebar-badge'>NEW</span>
                  </h2>
                </NavLink>
              </li>
            )}

            {/* Train Bot → Always available */}
            <li className={`sidebar-icon ${isActive(PATHS.CHATBOT)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
              <NavLink to={PATHS.CHATBOT}>
                <img src={CHATBOT_ICON} alt='chatbot-icon' />
                <h2 className={`hover-effect outfit-medium ${isActive(PATHS.CHATBOT) ? 'hoverActive' : ''}`} id='train-bot'>
                  Train Bot
                </h2>
              </NavLink>
            </li>

            {/* Auto Response Submenu */}
            <div id='collapselivechat' className='accordion-collapse collapse' aria-labelledby='train-bot' data-bs-parent='#accordionlivechat'>
              <div className='accordion-body'>
                <div className='accordion-modify'>
                  <Link to={PATHS.AGENT_KNOWLEDGE_BASE} className={`outfit-semibold ${isActive(PATHS.AGENT_KNOWLEDGE_BASE) ? 'hoverActive' : ''}`}>
                    Auto Response
                  </Link>
                </div>
              </div>
            </div>

            {/* Leads → Growth and above */}
            {planLevel >= PLAN_LEVELS.Growth && (
              <li className={`sidebar-icon ${isActive(PATHS.LEADS)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                <NavLink to={PATHS.LEADS}>
                  <img src={LEAD_ICON} alt='lead-icon' />
                  <h2 className={`hover-effect outfit-medium ${isActive(PATHS.LEADS) && 'hoverActive'}`}>Generated Leads</h2>
                </NavLink>
              </li>
            )}

            {/* Integrations → Growth and above */}
            {planLevel >= PLAN_LEVELS.Growth && (
              <li className={`sidebar-icon ${isActive(PATHS.INTEGRATIONS)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                <NavLink to={PATHS.INTEGRATIONS}>
                  <img src={INTEGRATION_ICON} alt='integration-icon' />
                  <h2 className={`hover-effect outfit-medium ${isActive(PATHS.INTEGRATIONS) && 'hoverActive'}`}>Integrations</h2>
                </NavLink>
              </li>
            )}
            <li className={`sidebar-icon ${isActive(PATHS.PRICING)}`} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
              <NavLink to={PATHS.PRICING}>
                <img src={PRICING_ICON} alt='integration-icon' />
                <h2 className={`hover-effect outfit-medium ${isActive(PATHS.PRICING) && 'hoverActive'}`}>Pricing</h2>
              </NavLink>
            </li>

            {/* Sidebar Bottom Box */}
            <div className='d-flex flex-column gap-3 mt-auto'>
              {(isProgressActive || showPopup) && <ProgressBar progress={progress} urlStats={urlStats} />}
              <div className='sidebar-box position-relative mt-auto'>
                <div className='plan-header d-flex align-items-center mb-2'>
                  <div className='d-flex align-items-center gap-2'>
                    <img src={PLAN_ICON} alt='plan-icon' className='plan-icon' />
                    <h6 className='mb-0 text-white font14 outfit-semibold'>{subscriptionName} Plan</h6>
                  </div>
                  {planLevel === PLAN_LEVELS.Free && <span className='limited-pill'>Limited</span>}
                </div>

                {/* Conversations */}
                <div className='d-flex align-items-center sidebar-box-text'>
                  <img src={CHAT_ICON} alt='chat-img' className='sidebar-box-logo' />
                  <div className='text-start'>
                    <h6 className='mb-0 outfit-semibold text-white font14'>Conversations</h6>
                    <span className='sidebar-box-counter outfit-regular'>
                      {subsFlag?.conversionData?.con || 0} / {subsFlag?.conversionData?.total || 0}
                    </span>
                  </div>
                </div>

                {/* Word Count */}
                <div className='d-flex align-items-center sidebar-box-text mt-2'>
                  <img src={DOCS_ICON} alt='docs-img' className='sidebar-box-logo' />
                  <div className='text-start'>
                    <h6 className='mb-0 outfit-semibold font14'>Word Count</h6>
                    <span className='sidebar-box-counter outfit-regular'>
                      <span className={(subsFlag?.conversionData?.word_count || 0) > (subsFlag?.conversionData?.word_limit || 0) ? 'outfit-bold' : 'outfit-regular'}>
                        {subsFlag?.conversionData?.word_count || 0}
                      </span>
                      {' / '} {subsFlag?.conversionData?.word_limit || 0}
                    </span>
                  </div>
                </div>

                {/* Upgrade Button */}
                {planLevel < PLAN_LEVELS.Pro && (
                  <div className='mt-2 plan-btn'>
                    <button className='outfit-semibold font14 w-100' style={{ borderRadius: '12px' }} onClick={() => Navigate('/pricing')}>
                      Upgrade
                    </button>
                  </div>
                )}
              </div>
            </div>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
